import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Globe, Eye, EyeOff } from 'lucide-react';
import {
  airportCodes, filterOptions, buildRouteData, routeBounds
} from '../utils/flightRoutes';

// Airport coordinates rarely change, so keep them for the session
const airportCache = {};
const OPEN_KEY = 'flightRoutesMapOpen';

const TILES = {
  light: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  },
};

const COLORS = {
  light: { flown: '#2563eb', scheduled: '#d97706', airport: '#0369a1', airportEdge: '#ffffff' },
  dark:  { flown: '#60a5fa', scheduled: '#fbbf24', airport: '#7dd3fc', airportEdge: '#0f172a' },
};

const readOpen = () => {
  try { return localStorage.getItem(OPEN_KEY) === 'true'; } catch { return false; }
};

// Follows the app's dark mode, which toggles the `dark` class on <html>
function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

// Zoom to fit whatever routes are showing
function FitToRoutes({ bounds }) {
  const map = useMap();
  const key = bounds ? bounds.flat().map(n => n.toFixed(3)).join(',') : '';
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 8 });
    // key captures the bounds' value; the array itself is rebuilt every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

// Scroll-wheel zoom only after the map is clicked, so scrolling the page
// past the map doesn't zoom it; leaving the map turns it off again
function ScrollZoomGate({ onChange }) {
  const map = useMap();
  useEffect(() => {
    const enable = () => { map.scrollWheelZoom.enable(); onChange(true); };
    const disable = () => { map.scrollWheelZoom.disable(); onChange(false); };
    map.on('click', enable);
    map.on('mouseout', disable);
    return () => {
      map.off('click', enable);
      map.off('mouseout', disable);
    };
  }, [map, onChange]);
  return null;
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const selectClasses = 'px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200';

const FlightRoutesMap = ({ getAuthHeaders }) => {
  const [open, setOpen] = useState(readOpen);
  const [loading, setLoading] = useState(false);
  const [flights, setFlights] = useState([]);
  const [airports, setAirports] = useState({});
  const [filters, setFilters] = useState({ year: 'all', aircraftType: 'all', status: 'all' });
  const [scrollZoom, setScrollZoom] = useState(false);
  const isDark = useIsDark();
  const theme = isDark ? 'dark' : 'light';
  const colors = COLORS[theme];

  const load = useCallback(async () => {
    const authHeaders = getAuthHeaders();
    if (!authHeaders) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/flights/all', authHeaders);
      const list = Array.isArray(res.data) ? res.data : (res.data?.flights || res.data?.data || []);

      // One request for every airport we haven't looked up yet
      const missing = airportCodes(list).filter(code => !(code in airportCache));
      if (missing.length > 0) {
        const { data } = await axios.post('/api/airports/lookup', { codes: missing }, authHeaders);
        missing.forEach(code => { airportCache[code] = data[code] || null; });
      }

      const placed = {};
      Object.entries(airportCache).forEach(([code, info]) => { if (info) placed[code] = info; });
      setAirports(placed);
      setFlights(list);
    } catch (error) {
      console.error('Error loading flight routes:', error);
      toast.error('Failed to load flight map data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Refresh whenever the map is opened so new flights appear
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem(OPEN_KEY, String(next)); } catch { /* preference only */ }
  };

  const options = useMemo(() => filterOptions(flights), [flights]);
  const data = useMemo(() => buildRouteData(flights, airports, filters), [flights, airports, filters]);
  const bounds = useMemo(() => routeBounds(data.routes, data.airports), [data]);
  const setFilter = (key) => (e) => setFilters(prev => ({ ...prev, [key]: e.target.value }));

  const highlight = (weight) => ({
    mouseover: (e) => { e.target.setStyle({ weight: weight + 2, opacity: 1 }); e.target.bringToFront(); },
    mouseout: (e) => { e.target.setStyle({ weight, opacity: 0.75 }); },
  });

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <Globe className="h-6 w-6 mr-2 text-sky-600" />
          Flight Routes
        </h2>
        <button
          onClick={toggle}
          aria-expanded={open}
          className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-sky-600 bg-sky-100 hover:bg-sky-200 rounded-lg transition-colors"
        >
          {open ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          <span>{open ? 'Hide Map' : 'Show Map'}</span>
        </button>
      </div>

      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <label className="sr-only" htmlFor="routes-year">Year</label>
            <select id="routes-year" value={filters.year} onChange={setFilter('year')} className={selectClasses}>
              <option value="all">All years</option>
              {options.years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <label className="sr-only" htmlFor="routes-aircraft">Aircraft type</label>
            <select id="routes-aircraft" value={filters.aircraftType} onChange={setFilter('aircraftType')} className={selectClasses}>
              <option value="all">All aircraft</option>
              {options.aircraftTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div role="radiogroup" aria-label="Flights to show" className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
              {[['all', 'All'], ['flown', 'Flown'], ['scheduled', 'Scheduled']].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={filters.status === value}
                  onClick={() => setFilters(prev => ({ ...prev, status: value }))}
                  className={`px-3 py-1.5 transition-colors ${
                    filters.status === value
                      ? 'bg-sky-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="map-behind-nav relative h-80 sm:h-96 lg:h-[30rem] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={4}
              minZoom={2}
              preferCanvas
              scrollWheelZoom={false}
              worldCopyJump
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer key={theme} url={TILES[theme].url} attribution={TILES[theme].attribution} />
              <FitToRoutes bounds={bounds} />
              <ScrollZoomGate onChange={setScrollZoom} />

              {data.routes.map(route => (
                <Polyline
                  key={`${route.key}-${theme}`}
                  positions={route.positions}
                  pathOptions={{
                    color: route.scheduledOnly ? colors.scheduled : colors.flown,
                    weight: route.weight,
                    opacity: 0.75,
                    dashArray: route.scheduledOnly ? '6 6' : null,
                  }}
                  eventHandlers={highlight(route.weight)}
                >
                  <Tooltip sticky>
                    <div className="text-sm">
                      <div className="font-semibold">{route.a} ↔ {route.b}</div>
                      <div>{plural(route.count, 'flight')} · {route.totalTime.toFixed(1)} hrs</div>
                      {route.scheduled > 0 && <div>{route.scheduled} scheduled</div>}
                    </div>
                  </Tooltip>
                </Polyline>
              ))}

              {data.airports.map(airport => (
                <CircleMarker
                  key={`${airport.code}-${theme}`}
                  center={airport.position}
                  radius={airport.radius}
                  pathOptions={{ color: colors.airportEdge, weight: 1.5, fillColor: colors.airport, fillOpacity: 0.9 }}
                >
                  <Tooltip direction="top" offset={[0, -airport.radius]}>
                    <div className="text-sm">
                      <div className="font-semibold">{airport.code}</div>
                      {airport.name && <div>{airport.name}{airport.city ? `, ${airport.city}` : ''}</div>}
                      <div>{plural(airport.visits, 'visit')}</div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>

            {loading && (
              <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70 dark:bg-gray-900/70">
                <div className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-600" />
                  Loading routes…
                </div>
              </div>
            )}
            {!loading && data.routes.length === 0 && (
              <div className="absolute inset-0 z-[500] flex items-center justify-center pointer-events-none">
                <span className="px-3 py-2 rounded-lg bg-white/90 dark:bg-gray-900/90 text-sm text-gray-700 dark:text-gray-200 shadow">
                  {flights.length === 0 ? 'No flights to map yet' : 'No routes match these filters'}
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-400">
            <span>
              {plural(data.routes.length, 'route')} · {plural(data.airports.length, 'airport')} · {plural(data.flightCount, 'flight')}
              {data.unplaced.length > 0 && (
                <span title={data.unplaced.join(', ')}> · {plural(data.unplaced.length, 'airport')} not found</span>
              )}
            </span>
            <span className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <svg width="22" height="6" aria-hidden="true"><line x1="0" y1="3" x2="22" y2="3" stroke={colors.flown} strokeWidth="3" /></svg>
                Flown
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="22" height="6" aria-hidden="true"><line x1="0" y1="3" x2="22" y2="3" stroke={colors.scheduled} strokeWidth="3" strokeDasharray="4 3" /></svg>
                Scheduled only
              </span>
              <span className="hidden sm:inline">{scrollZoom ? 'Scroll to zoom' : 'Click the map to scroll-zoom'}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default FlightRoutesMap;
